import { useEffect, useRef } from "react";

import { AppStore } from "@/store";
import { mdsSidebarWidths } from "@/design-system/foundations";

const COOLDOWN_MS = 600;

// Encapsulates auto-close/open sidebar logic and internal refs
export const useAutoCloseSidebarEffect = (effectiveWidth: number, store: AppStore) => {
  const sidebarAutoClosedRef = useRef<boolean>(false);
  const lastAutoToggleAtRef = useRef<number>(0);
  const keepSplitLockRef = useRef<boolean>(false);

  // Automatically close the left sidebar when there are 2 panes open
  // and the container is too narrow to display it all.
  // This frees up space for the center split view.
  // Track if the sidebar was auto-closed so it can be auto-reopened later.
  useEffect(() => {
    const now = performance.now();
    const inCooldown = now - lastAutoToggleAtRef.current < COOLDOWN_MS;
    if (inCooldown) return;

    const twoPanesOpen = store.navigation.tabs.panes.length >= 2;
    const tooNarrowForMultiPane =
      effectiveWidth > 0 && effectiveWidth < store.navigation.tabs.MIN_CONTAINER_WIDTH;
    if (twoPanesOpen && tooNarrowForMultiPane && store.interface.isSidebarOpen) {
      sidebarAutoClosedRef.current = true;
      lastAutoToggleAtRef.current = now;
      // Lock split for the duration of the sidebar animation (0.3s) plus buffer
      keepSplitLockRef.current = true;
      window.setTimeout(() => {
        keepSplitLockRef.current = false;
      }, 400);
      store.interface.setIsSidebarOpen(false);
    }
  }, [
    effectiveWidth,
    store.navigation.tabs.panes.length,
    store.navigation.tabs.MIN_CONTAINER_WIDTH,
    store.interface,
  ]);

  // Auto-reopen sidebar if we previously auto-closed it and width is sufficient again.
  useEffect(() => {
    const now = performance.now();
    const inCooldown = now - lastAutoToggleAtRef.current < COOLDOWN_MS;
    if (inCooldown) return;

    // Ensure that after reopening (expanded width), the center area remains >= MIN_CONTAINER_WIDTH
    const expandedWidth = mdsSidebarWidths().expanded;
    const widthSufficientAfterReopen =
      effectiveWidth > 0 &&
      effectiveWidth - expandedWidth >= store.navigation.tabs.MIN_CONTAINER_WIDTH;

    if (
      sidebarAutoClosedRef.current &&
      widthSufficientAfterReopen &&
      !store.interface.isSidebarOpen
    ) {
      lastAutoToggleAtRef.current = now;
      store.interface.setIsSidebarOpen(true);
      sidebarAutoClosedRef.current = false;
    }
  }, [
    effectiveWidth,
    store.navigation.tabs.MIN_CONTAINER_WIDTH,
    store.interface.isSidebarOpen,
    store.interface,
  ]);

  return { keepSplitLockRef };
};
